import React from 'react';
import { FaTimes } from 'react-icons/fa';
import { formatAUD } from '../../../utils/formatCurrency';
import '../styles/InventoryProcurement.css';

function formatDate(raw) {
  if (!raw) return '—';
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-AU', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

function statusLabel(status) {
  const s = String(status || '');
  if (s === 'PendingApproval') return 'Pending Approval';
  if (s === 'PartiallyReceived') return 'Partially Received';
  return s || '—';
}

function statusClass(status) {
  const s = String(status || '').toLowerCase();
  if (s.includes('draft')) return 'iproc-pill--draft';
  if (s.includes('pending')) return 'iproc-pill--pending';
  if (s.includes('partial')) return 'iproc-pill--partial';
  if (s.includes('received')) return 'iproc-pill--received';
  if (s.includes('cancel')) return 'iproc-pill--cancelled';
  return 'iproc-pill--ordered';
}

export default function PurchaseOrderDetailModal({ po, onClose, onEdit }) {
  if (!po) return null;

  const lines = po.lines || [];
  const ordered = lines.reduce((s, l) => s + (Number(l.quantity) || 0), 0);
  const received = lines.reduce((s, l) => s + (Number(l.quantityReceived) || 0), 0);
  const pct = ordered > 0 ? Math.round((received / ordered) * 100) : 0;

  return (
    <div className="iproc-overlay" onClick={onClose}>
      <div className="iproc-modal iproc-modal--wide" onClick={(e) => e.stopPropagation()}>
        <header className="iproc-modal-header">
          <h2>Purchase Order — {po.poNumber}</h2>
          <button type="button" className="iproc-modal-close" onClick={onClose}>
            <FaTimes />
          </button>
        </header>
        <div className="iproc-modal-body">
          <div className="iproc-badges">
            <span className="iproc-code-badge">{po.poNumber}</span>
            <span className={`iproc-pill ${statusClass(po.status)}`}>{statusLabel(po.status)}</span>
          </div>

          <div className="iproc-detail-cards">
            <div className="iproc-detail-card">
              <b>Supplier</b>
              <span>{po.supplier?.name || '—'}</span>
            </div>
            <div className="iproc-detail-card">
              <b>Created</b>
              <span>{formatDate(po.orderDate)}</span>
            </div>
            <div className="iproc-detail-card">
              <b>Expected Delivery</b>
              <span>{formatDate(po.expectedDelivery)}</span>
            </div>
            <div className="iproc-detail-card">
              <b>Total Items</b>
              <span>{po.lineCount ?? lines.length}</span>
            </div>
            <div className="iproc-detail-card">
              <b>Total Value</b>
              <span>{formatAUD(po.total)}</span>
            </div>
            <div className="iproc-detail-card">
              <b>Status</b>
              <span>{statusLabel(po.status)}</span>
            </div>
          </div>

          <h3 className="iproc-section-title">Line Items</h3>
          <table className="iproc-table">
            <thead>
              <tr>
                <th>Product</th>
                <th>Qty Ordered</th>
                <th>Qty Received</th>
                <th>Unit Cost</th>
                <th>Total</th>
              </tr>
            </thead>
            <tbody>
              {lines.length === 0 ? (
                <tr><td colSpan="5" className="iproc-empty">No line items</td></tr>
              ) : (
                lines.map((l) => {
                  const q = Number(l.quantity) || 0;
                  const r = Number(l.quantityReceived) || 0;
                  const recvClass =
                    r >= q && q > 0
                      ? 'iproc-recv--full'
                      : r > 0
                        ? 'iproc-recv--partial'
                        : '';
                  return (
                    <tr key={l.id}>
                      <td>{l.description}</td>
                      <td>{q}</td>
                      <td className={recvClass}>{r}</td>
                      <td>{formatAUD(l.unitCost)}</td>
                      <td>{formatAUD(l.lineTotal)}</td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>

          <h3 className="iproc-section-title">Delivery Progress</h3>
          <div className="iproc-progress-wrap">
            <div className="iproc-progress">
              <div className="iproc-progress-bar" style={{ width: `${Math.min(100, pct)}%` }} />
            </div>
            <span>{pct}% received</span>
          </div>

          <div className="iproc-modal-footer">
            <button type="button" className="iproc-btn" onClick={onClose}>Close</button>
            <button
              type="button"
              className="iproc-btn iproc-btn--primary"
              onClick={() => onEdit?.(po)}
            >
              Edit PO
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
